import { useCallback, useEffect, useRef, useState } from 'react';

import { probeHardwareService } from '../domain/labelPrinting';
import { canReadScale, readScaleWeight, type ScaleResult } from '../domain/scaleReader';

/**
 * Whether this workstation can read its scale, and the one read in flight.
 *
 * All the rules live in `domain/scaleReader`; this holds only the React plumbing. The service is
 * probed once per mount; a read touches the serial port only when the operator asks.
 */
export interface UseScaleReader {
    /** The service is ready, paired and offers `scale.weight`. */
    available: boolean;
    /** Reads one stable weight; null when a read is already running. */
    read: () => Promise<ScaleResult | null>;
}

export function useScaleReader(): UseScaleReader {
    const [available, setAvailable] = useState(false);
    const inFlight = useRef(false);

    useEffect(() => {
        let active = true;

        void probeHardwareService(window.fetch.bind(window)).then(status => {
            if (active) {
                setAvailable(canReadScale(status));
            }
        });

        return () => {
            active = false;
        };
    }, []);

    const read = useCallback(async () => {
        // A second F2 while the first read runs is ignored here rather than refused by the service:
        // the first read is already filling a field.
        if (inFlight.current) {
            return null;
        }

        inFlight.current = true;

        try {
            return await readScaleWeight(window.fetch.bind(window));
        } finally {
            inFlight.current = false;
        }
    }, []);

    return { available, read };
}
